class JensenPortalHeader extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-jm-menu-toggle]');
    this.nav = this.querySelector('[data-jm-nav]');
    if (!this.button || !this.nav) return;

    this.button.addEventListener('click', () => {
      const isOpen = this.nav.classList.toggle('is-open');
      this.button.setAttribute('aria-expanded', String(isOpen));
    });
  }
}

if (!customElements.get('jensen-portal-header')) {
  customElements.define('jensen-portal-header', JensenPortalHeader);
}

document.querySelectorAll('[data-jm-login-link]').forEach((link) => {
  link.addEventListener('click', () => {
    const card = link.closest('.jm-login-card');
    const email = card?.querySelector('[data-jm-login-email]')?.value?.trim();
    if (!email) return;

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('login_hint', email);
      link.href = url.toString();
    } catch (error) {
      // Keep the default account link if the URL cannot be parsed.
    }
  });
});

const revealTargets = document.querySelectorAll('.jm-reveal');
if (revealTargets.length) {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );

    revealTargets.forEach((target) => observer.observe(target));
  } else {
    revealTargets.forEach((target) => target.classList.add('is-visible'));
  }
}

const productHandleFromUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const marker = '/products/';
    if (!parsed.pathname.includes(marker)) return '';
    return parsed.pathname.split(marker).pop().split('/').shift();
  } catch (error) {
    return '';
  }
};

const escapeHtml = (value) =>
  String(value || '').replace(/[&<>"']/g, (character) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character];
  });

const moneyPattern = /(?:[$\u20ac\u00a3\u20b9]\s?\d[\d,.]*(?:\.\d{2})?|\d[\d,.]*(?:\.\d{2})?\s?(?:USD|CAD|EUR|GBP))/i;
const productJsonCache = new Map();

const cleanCatalogText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const isCatalogNoise = (value, title = '') => {
  const text = cleanCatalogText(value);
  const normalized = text.toLowerCase();

  if (!text || text === title) return true;
  if (text.length > 180) return true;
  if (/[{};]/.test(text) || normalized.startsWith('.') || normalized.includes('custom-color-quick-add')) return true;
  if (moneyPattern.test(text)) return true;

  return [
    'add',
    'quick add',
    'view product',
    'view product details',
    'add to cart',
    'default title',
    'choose',
    'sold out',
    'unavailable',
    'availability',
    'price',
    'sort',
    'items',
    'grid view',
    'list view',
  ].includes(normalized);
};

const findCatalogCard = (link) => {
  let best = link.parentElement;
  let current = link;

  for (let index = 0; index < 9 && current; index += 1) {
    const text = String(current.textContent || '');
    const hasImage = Boolean(current.querySelector('img'));
    const hasPrice = moneyPattern.test(text);

    if (hasImage && hasPrice) return current;
    if (hasImage) best = current;
    current = current.parentElement;
  }

  return best || link;
};

const extractCatalogPrice = (card) => {
  if (!card) return '';

  const selectors = ['[data-price]', '[class*="price"]', 'price-list', '.price', '.price-item', '[data-product-price]'];

  for (const selector of selectors) {
    const matches = Array.from(card.querySelectorAll(selector));
    for (const match of matches) {
      const text = cleanCatalogText(match.dataset.price || match.textContent);
      const price = text.match(moneyPattern);
      if (price) return price[0];
    }
  }

  const price = cleanCatalogText(card.textContent).match(moneyPattern);
  return price ? price[0] : '';
};

const extractCatalogProducts = (doc, limit) => {
  const main = doc.querySelector('main') || doc.body;
  const productLinks = Array.from(main.querySelectorAll('a[href*="/products/"]'));
  const products = new Map();

  productLinks.forEach((link) => {
    const handle = productHandleFromUrl(link.href);
    if (!handle || products.has(handle)) return;

    const card = link.closest('product-card') || link.closest('[class*="product-card"]') || findCatalogCard(link);
    const image = (card || link).querySelector('img');
    const titleNode = card?.querySelector('h3, h2, .product-title, [class*="title"]');
    const textCandidates = [titleNode?.textContent, link.textContent, image?.alt];
    const title = textCandidates.map(cleanCatalogText).find((text) => text && !isCatalogNoise(text));
    const cardClone = card?.cloneNode(true);
    cardClone?.querySelectorAll('style, script, noscript, svg, button, select, form').forEach((node) => node.remove());
    const cardText = String(cardClone?.textContent || '');
    const description = cardText
      .split('\n')
      .map(cleanCatalogText)
      .filter(Boolean)
      .find((part) => !isCatalogNoise(part, title));

    products.set(handle, {
      handle,
      title: title || handle.replace(/-/g, ' '),
      url: new URL(link.getAttribute('href'), window.location.origin).pathname,
      image: image?.currentSrc || image?.src || '',
      imageAlt: image?.alt || title || '',
      description: description || '',
      price: extractCatalogPrice(card),
    });
  });

  return Array.from(products.values()).slice(0, limit);
};

const renderSyncedCompanyProducts = (section, products) => {
  const grid = section.querySelector('[data-jm-company-grid]');
  let empty = section.querySelector('[data-jm-company-empty]');
  if (!grid) return;

  if (!empty) {
    empty = document.createElement('div');
    empty.className = 'jm-company__empty';
    empty.dataset.jmCompanyEmpty = '';
    grid.after(empty);
  }

  const buttonText = section.dataset.jmButtonText || 'View Product';
  const showPrice = section.dataset.jmShowPrice === 'true';

  if (!products.length) {
    grid.hidden = true;
    empty.hidden = false;
    empty.innerHTML = `<strong>${escapeHtml(section.dataset.jmEmptyHeading || 'No assigned products available.')}</strong><span>${escapeHtml(
      section.dataset.jmEmptyText || 'Contact your account manager to confirm catalog access for this company.'
    )}</span>`;
    section.classList.add('is-locksmith-ready');
    return;
  }

  grid.hidden = false;
  empty.hidden = true;
  grid.innerHTML = products
    .map(
      (product) => `
        <article class="jm-product-card jm-product-card--synced jm-reveal is-visible" data-product-handle="${escapeHtml(product.handle)}">
          <div class="jm-product-card__media">
            <a class="jm-product-card__media-link" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">
              ${
                product.image
                  ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.imageAlt || product.title)}" loading="lazy">`
                  : ''
              }
            </a>
            <button class="jm-quick-add-trigger" type="button" data-jm-quick-add="${escapeHtml(product.handle)}" data-jm-price="${escapeHtml(
              product.price
            )}" aria-label="Quick add ${escapeHtml(product.title)}">
              <span aria-hidden="true"></span>
            </button>
          </div>
          <h3>${escapeHtml(product.title)}</h3>
          ${product.description ? `<p class="jm-product-card__desc">${escapeHtml(product.description)}</p>` : ''}
          ${showPrice && product.price ? `<div class="jm-product-card__price">${escapeHtml(product.price)}</div>` : '<div class="jm-product-card__price">&nbsp;</div>'}
          <div class="jm-product-card__action">
            <a class="jm-product-card__link jm-product-card__link--outline" href="${escapeHtml(product.url)}">${escapeHtml(buttonText)}</a>
          </div>
        </article>`
    )
    .join('');
  section.classList.add('is-locksmith-ready');
};

const formatMoney = (cents) => {
  const amount = Number(cents || 0) / 100;
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  }).format(amount);
};

const moneyToCents = (value) => {
  const number = String(value || '').match(/[\d,.]+/);
  if (!number) return null;
  return Math.round(Number(number[0].replace(/,/g, '')) * 100);
};

const fetchProductJson = async (handle) => {
  if (!handle) return null;
  if (!productJsonCache.has(handle)) {
    productJsonCache.set(
      handle,
      fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load product.');
          return response.json();
        })
        .catch(() => null)
    );
  }

  return productJsonCache.get(handle);
};

const enrichCatalogProducts = async (products) =>
  Promise.all(
    products.map(async (product) => {
      if (product.price && product.image) return product;

      const productJson = await fetchProductJson(product.handle);
      if (!productJson) return product;

      const variant = productJson.variants?.find((item) => item.available) || productJson.variants?.[0];
      const description = cleanCatalogText((productJson.description || '').replace(/<[^>]*>/g, ''));

      return {
        ...product,
        image: product.image || productJson.featured_image || productJson.images?.[0] || '',
        imageAlt: product.imageAlt || productJson.title || product.title,
        description: product.description || description.slice(0, 150),
        price: product.price || (variant ? formatMoney(variant.price) : ''),
      };
    })
  );

document.querySelectorAll('[data-jm-locksmith-sync="true"]').forEach(async (section) => {
  const url = section.dataset.jmLocksmithSyncUrl;
  const limit = Number(section.dataset.jmProductLimit || 3);
  if (!url) return;

  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Catalog fetch failed: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const products = await enrichCatalogProducts(extractCatalogProducts(doc, limit));
    renderSyncedCompanyProducts(section, products);
  } catch (error) {
    const currentCards = Array.from(section.querySelectorAll('[data-product-handle]'));
    if (currentCards.length) {
      section.classList.add('is-locksmith-ready');
    } else {
      renderSyncedCompanyProducts(section, []);
    }
  }
});

const ensureQuickAddModal = () => {
  let modal = document.querySelector('[data-jm-quick-add-modal]');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.className = 'jm-quick-modal';
  modal.dataset.jmQuickAddModal = '';
  modal.hidden = true;
  modal.innerHTML = `
    <div class="jm-quick-modal__backdrop" data-jm-quick-close></div>
    <div class="jm-quick-modal__panel" role="dialog" aria-modal="true" aria-labelledby="jm-quick-title">
      <button class="jm-quick-modal__close" type="button" data-jm-quick-close aria-label="Close quick add">×</button>
      <div class="jm-quick-modal__body" data-jm-quick-body></div>
    </div>`;
  document.body.append(modal);
  modal.addEventListener('click', (event) => {
    if (event.target.closest('[data-jm-quick-close]')) {
      modal.hidden = true;
      document.documentElement.classList.remove('jm-quick-modal-open');
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) {
      modal.hidden = true;
      document.documentElement.classList.remove('jm-quick-modal-open');
    }
  });
  return modal;
};

const updateHeaderCartCount = async () => {
  try {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) return;
    const cart = await response.json();
    const cartLink = document.querySelector('.jm-header__icon-link[href*="/cart"]');
    if (!cartLink) return;
    let badge = cartLink.querySelector('.jm-header__cart-count');
    if (cart.item_count > 0 && !badge) {
      badge = document.createElement('span');
      badge.className = 'jm-header__cart-count';
      cartLink.append(badge);
    }
    if (badge) {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count < 1;
    }
  } catch (error) {
    // Cart count refresh is best-effort.
  }
};

const renderQuickAddProduct = (modal, product, priceOverride = '') => {
  const body = modal.querySelector('[data-jm-quick-body]');
  const availableVariants = product.variants.filter((variant) => variant.available);
  const variants = availableVariants.length ? availableVariants : product.variants;
  const firstVariant = variants[0];
  const featuredImage = product.featured_image || product.images?.[0] || '';
  const hasMeaningfulVariants = variants.length > 1 || (firstVariant && firstVariant.title !== 'Default Title');
  const initialUnitPrice = moneyToCents(priceOverride) ?? Number(firstVariant?.price || 0);

  body.innerHTML = `
    <div class="jm-quick-product">
      <div class="jm-quick-product__image">${featuredImage ? `<img src="${escapeHtml(featuredImage)}" alt="${escapeHtml(product.title)}">` : ''}</div>
      <div class="jm-quick-product__content">
        <p class="jm-eyebrow">Partner Quick Add</p>
        <h2 id="jm-quick-title">${escapeHtml(product.title)}</h2>
        ${
          (product.description || '').replace(/<[^>]*>/g, '').trim()
            ? `<p class="jm-quick-product__summary">${escapeHtml((product.description || '').replace(/<[^>]*>/g, '').trim()).slice(0, 150)}</p>`
            : ''
        }
        <form class="jm-quick-form" data-jm-quick-form>
          ${
            hasMeaningfulVariants
              ? `<label>
                  <span>Variant</span>
                  <select name="id" ${variants.length < 2 ? 'disabled' : ''}>
                    ${variants
                      .map(
                        (variant) =>
                          `<option value="${variant.id}" data-price="${variant.price}" ${variant.id === firstVariant.id ? 'selected' : ''}>${escapeHtml(
                            variant.title
                          )}${variant.available ? '' : ' - unavailable'}</option>`
                      )
                      .join('')}
                  </select>
                </label>`
              : `<input type="hidden" name="id" value="${firstVariant?.id || ''}">`
          }
          <div class="jm-quick-product__purchase-row">
            <label>
              <span>Quantity</span>
              <span class="jm-quantity-stepper" data-jm-quantity-stepper>
                <button type="button" data-jm-quantity-decrease aria-label="Decrease quantity">-</button>
                <input name="quantity" type="number" min="1" step="1" value="1" inputmode="numeric">
                <button type="button" data-jm-quantity-increase aria-label="Increase quantity">+</button>
              </span>
            </label>
            <div class="jm-quick-product__meta">
              <strong data-jm-quick-price data-unit-price="${initialUnitPrice}">${escapeHtml(formatMoney(initialUnitPrice))}</strong>
            </div>
          </div>
          <div class="jm-quick-product__total">
            <span>Total</span>
            <strong data-jm-quick-total>${escapeHtml(formatMoney(initialUnitPrice))}</strong>
          </div>
          <button class="jm-btn jm-btn--primary" type="submit">Add to Cart</button>
          <a class="jm-quick-product__details" href="/products/${escapeHtml(product.handle)}">View product details</a>
          <p class="jm-quick-product__status" data-jm-quick-status role="status"></p>
        </form>
      </div>
    </div>`;

  const form = body.querySelector('[data-jm-quick-form]');
  const select = form.querySelector('select[name="id"]');
  const price = body.querySelector('[data-jm-quick-price]');
  const total = body.querySelector('[data-jm-quick-total]');
  const status = body.querySelector('[data-jm-quick-status]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const decreaseQuantity = form.querySelector('[data-jm-quantity-decrease]');
  const increaseQuantity = form.querySelector('[data-jm-quantity-increase]');
  const submit = form.querySelector('button[type="submit"]');
  let resetSubmitTimer;
  const updateTotal = () => {
    const quantity = Math.max(1, Number(quantityInput.value || 1));
    const unitPrice = Number(price.dataset.unitPrice || 0);
    price.textContent = formatMoney(unitPrice);
    total.textContent = formatMoney(unitPrice * quantity);
  };
  if (select) {
    select.addEventListener('change', () => {
      if (!priceOverride) {
        price.dataset.unitPrice = String(select.selectedOptions[0]?.dataset.price || 0);
      }
      updateTotal();
    });
  }
  const updateQuantity = (change) => {
    const current = Number(quantityInput.value || 1);
    const next = Math.max(1, current + change);
    quantityInput.value = String(next);
    updateTotal();
  };
  decreaseQuantity?.addEventListener('click', () => updateQuantity(-1));
  increaseQuantity?.addEventListener('click', () => updateQuantity(1));
  quantityInput.addEventListener('input', () => {
    if (Number(quantityInput.value || 0) < 1) quantityInput.value = '1';
    updateTotal();
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    status.textContent = '';
    window.clearTimeout(resetSubmitTimer);
    submit.classList.remove('is-added');
    submit.disabled = true;
    submit.textContent = 'Adding...';
    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this product.');
      }
      submit.classList.add('is-added');
      submit.textContent = 'Added';
      await updateHeaderCartCount();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      resetSubmitTimer = window.setTimeout(() => {
        submit.classList.remove('is-added');
        submit.textContent = 'Add to Cart';
      }, 1400);
    } catch (error) {
      status.textContent = error.message || 'Unable to add this product.';
      submit.textContent = 'Add to Cart';
    } finally {
      submit.disabled = false;
    }
  });
};

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-jm-quick-add]');
  if (!trigger) return;
  event.preventDefault();
  event.stopPropagation();

  const handle = trigger.dataset.jmQuickAdd;
  const priceOverride = trigger.dataset.jmPrice || '';
  if (!handle) return;
  const modal = ensureQuickAddModal();
  const body = modal.querySelector('[data-jm-quick-body]');
  body.innerHTML = '<div class="jm-quick-modal__loading">Loading product...</div>';
  modal.hidden = false;
  document.documentElement.classList.add('jm-quick-modal-open');

  try {
    const product = await fetchProductJson(handle);
    if (!product) throw new Error('Unable to load product.');
    renderQuickAddProduct(modal, product, priceOverride);
  } catch (error) {
    body.innerHTML = `<div class="jm-company__empty"><strong>Product unavailable.</strong><span>${escapeHtml(
      error.message || 'Open the product page to continue.'
    )}</span></div>`;
  }
});

document.querySelectorAll('[data-jm-product-section]').forEach((section) => {
  const productData = section.querySelector('[data-jm-product-json]');
  const product = productData ? JSON.parse(productData.textContent || '{}') : {};
  const form = section.querySelector('[data-jm-product-form]');
  const mainMedia = section.querySelector('[data-jm-main-media]');
  const thumbs = Array.from(section.querySelectorAll('[data-media-src]'));
  const optionGroups = Array.from(section.querySelectorAll('[data-option-index]'));
  const variantInput = section.querySelector('[data-jm-variant-id]');
  const quantityInput = section.querySelector('[data-jm-product-quantity]');
  const price = section.querySelector('[data-jm-product-price]');
  const sku = section.querySelector('[data-jm-product-sku]');
  const availability = section.querySelector('[data-jm-product-availability]');
  const submit = section.querySelector('[data-jm-product-submit]');
  const stickySubmit = section.querySelector('[data-jm-sticky-submit]');
  const stickyBar = section.querySelector('[data-jm-product-sticky]');
  const stickyVariant = section.querySelector('[data-jm-sticky-variant]');
  const status = section.querySelector('[data-jm-product-status]');
  let selectedVariant = product.variants?.find((variant) => String(variant.id) === variantInput?.value) || product.variants?.[0];
  let resetTimer;

  const selectedOptions = () =>
    optionGroups.map((group) => group.querySelector('[aria-pressed="true"]')?.dataset.optionValue || '');

  const findVariant = () => {
    const options = selectedOptions();
    return product.variants?.find((variant) => variant.options.every((value, index) => value === options[index]));
  };

  const setMainMedia = (src, alt = '') => {
    if (!mainMedia || !src || !('src' in mainMedia)) return;
    mainMedia.style.opacity = '0.3';
    window.setTimeout(() => {
      mainMedia.src = src;
      mainMedia.alt = alt || product.title || '';
      mainMedia.style.opacity = '1';
    }, 120);
  };

  const setSubmitState = (available) => {
    [submit, stickySubmit].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
      button.textContent = available ? 'Add to Cart' : 'Unavailable';
    });
  };

  const updateProductState = () => {
    selectedVariant = findVariant() || selectedVariant;
    if (!selectedVariant) return;

    if (variantInput) variantInput.value = selectedVariant.id;
    if (price) price.textContent = formatMoney(selectedVariant.price);
    if (sku) {
      sku.hidden = !selectedVariant.sku;
      sku.innerHTML = `SKU: <strong>${escapeHtml(selectedVariant.sku || '')}</strong>`;
    }
    if (availability) {
      availability.classList.toggle('is-available', Boolean(selectedVariant.available));
      availability.classList.toggle('is-unavailable', !selectedVariant.available);
      availability.textContent = selectedVariant.available ? 'Available' : 'Unavailable';
    }
    if (stickyVariant) stickyVariant.textContent = selectedVariant.title || '';
    setSubmitState(Boolean(selectedVariant.available));

    const variantImage = selectedVariant.featured_image?.src || selectedVariant.featured_media?.preview_image?.src;
    if (variantImage) setMainMedia(variantImage, selectedVariant.featured_image?.alt || product.title);
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      thumbs.forEach((item) => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
      setMainMedia(thumb.dataset.mediaSrc, thumb.dataset.mediaAlt);
    });
  });

  optionGroups.forEach((group) => {
    group.addEventListener('click', (event) => {
      const button = event.target.closest('[data-option-value]');
      if (!button) return;
      group.querySelectorAll('[data-option-value]').forEach((item) => {
        item.classList.toggle('is-selected', item === button);
        item.setAttribute('aria-pressed', String(item === button));
      });
      updateProductState();
    });
  });

  const changeQuantity = (change) => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) + change));
  };

  section.querySelector('[data-jm-product-quantity-decrease]')?.addEventListener('click', () => changeQuantity(-1));
  section.querySelector('[data-jm-product-quantity-increase]')?.addEventListener('click', () => changeQuantity(1));
  quantityInput?.addEventListener('input', () => {
    if (Number(quantityInput.value || 0) < 1) quantityInput.value = '1';
  });

  const submitProduct = async () => {
    if (!form || !selectedVariant || !selectedVariant.available || !submit) return;
    window.clearTimeout(resetTimer);
    status.textContent = '';
    submit.disabled = true;
    submit.textContent = 'Adding...';
    if (stickySubmit) stickySubmit.textContent = 'Adding...';

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this product.');
      }
      submit.classList.add('is-added');
      submit.textContent = 'Added';
      if (stickySubmit) stickySubmit.textContent = 'Added';
      await updateHeaderCartCount();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      resetTimer = window.setTimeout(() => {
        submit.classList.remove('is-added');
        setSubmitState(Boolean(selectedVariant.available));
      }, 1400);
    } catch (error) {
      status.textContent = error.message || 'Unable to add this product.';
      setSubmitState(Boolean(selectedVariant.available));
    } finally {
      submit.disabled = false;
    }
  };

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    submitProduct();
  });

  stickySubmit?.addEventListener('click', submitProduct);

  if (stickyBar && form && 'IntersectionObserver' in window) {
    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    stickyObserver.observe(form);
  }

  updateProductState();
});

document.querySelectorAll('[data-jm-faq-list]').forEach((list) => {
  list.addEventListener('toggle', (event) => {
    if (event.target.tagName !== 'DETAILS' || !event.target.open) return;
    list.querySelectorAll('details[open]').forEach((details) => {
      if (details !== event.target) details.removeAttribute('open');
    });
  }, true);
});
